import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../components/ui/Button.js';
import { Modal } from '../../../components/ui/Modal.js';
import { api } from '../../../services/api.js';
import { type ImpactUnpaid, type ImpactWorkflow, impactPath } from '../settingsApi.js';
import type { BookingTimers, Mode } from '../settingsTypes.js';

export type Automation = 'unpaid_bookings' | 'auto_workflow';

export interface OnRequest {
  automation: Automation;
  from: Mode;
  timers?: BookingTimers;
}

const NAMES: Record<Automation, string> = {
  unpaid_bookings: 'the unpaid auto-cancel',
  auto_workflow: 'the automatic booking workflow',
};

const MODE: Record<Mode, string> = { off: 'Off', count: 'Count only', on: 'On' };

function impactText(automation: Automation, data: ImpactUnpaid | ImpactWorkflow | undefined) {
  if (!data) return null;
  const atLeast = data.capped ? 'at least ' : '';
  if (automation === 'unpaid_bookings') {
    const n = (data as ImpactUnpaid).count;
    if (n === 0 && !data.capped) return 'Its first run would cancel nothing now.';
    return `Its first run would cancel ${atLeast}${n} ${n === 1 ? 'booking' : 'bookings'} now, and tell each customer.`;
  }
  const w = data as ImpactWorkflow;
  if (w.check_outs === 0 && w.closes === 0 && !w.capped)
    return 'Its first run would move nothing now.';
  return `Its first run would check out ${atLeast}${w.check_outs} and complete ${atLeast}${w.closes} ${
    w.closes === 1 ? 'rental' : 'rentals'
  } now. Customers are not sent a message.`;
}

const WHAT_ON_DOES: Record<Automation, string> = {
  unpaid_bookings:
    'On cancels every booking still unpaid after the set minutes, frees its car and tells the customer. A customer who is paying right now is never cancelled.',
  auto_workflow:
    'On checks out paid bookings 15 minutes before pickup and completes them when the rental ends, without staff pressing the buttons.',
};

/**
 * 356 (US4, FR-011): turning an automation On is a decision. States what On does and, where the platform
 * can count it, what the first run would act on now. Keep / Count only first (only from Off) / Turn on.
 */
export function ConfirmOnDialog({
  request,
  onKeep,
  onCountFirst,
  onTurnOn,
  saving,
}: {
  request: OnRequest | null;
  onKeep: () => void;
  onCountFirst: () => void;
  onTurnOn: () => void;
  saving: boolean;
}) {
  const impact = useQuery({
    queryKey: ['platform-config', 'impact', request?.automation, request?.timers ?? null],
    queryFn: () =>
      api.get<ImpactUnpaid | ImpactWorkflow>(
        impactPath(request?.automation ?? 'auto_workflow', request?.timers),
      ),
    enabled: request !== null,
    retry: false,
    staleTime: 0,
  });
  if (!request) return null;
  const text = impact.isError ? null : impactText(request.automation, impact.data);
  return (
    <Modal
      open
      onClose={onKeep}
      title={`Turn on ${NAMES[request.automation]}?`}
      size="md"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onKeep} disabled={saving}>
            {`Keep ${MODE[request.from]}`}
          </Button>
          {request.from === 'off' && (
            <Button type="button" variant="secondary" onClick={onCountFirst} disabled={saving}>
              Count only first
            </Button>
          )}
          <Button type="button" onClick={onTurnOn} disabled={saving}>
            {saving ? 'Saving…' : 'Turn on'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-charcoal">
        <p>
          <span className="font-semibold">{MODE[request.from]}</span> →{' '}
          <span className="font-semibold text-semantic-success-text">On</span>
        </p>
        <p>{WHAT_ON_DOES[request.automation]}</p>
        {impact.isLoading && <p className="text-steel">Counting what it would act on now…</p>}
        {text && (
          <p data-testid="confirm-impact" className="rounded-lg bg-brand-yellow-soft px-3 py-2">
            {text}
          </p>
        )}
      </div>
    </Modal>
  );
}
